import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {Group, Select, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {forwardRef} from 'react';

import {assert} from 'Asserts';
import {EnumOptions, GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import {ButtonSize} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {JSONValue} from 'util/Types';

import CyclingSelector from '../CyclingSelector';
import {EnumUISchema} from '../StepConfigEditorTypes';

interface EnumEditorProps {
  config: string | number | null;
  configShape: GetPipelineConfigSpecType<'enum'>;
  onChangeConfig: (config: JSONValue) => void;
  uiSchema?: EnumUISchema;
  disabled?: boolean;
  buttonSize?: ButtonSize;
  isExpandable?: boolean;
  isExpanded?: boolean;
  dataTestAttrs?: DataTestAttrs;
}
interface ItemProps {
  icon?: BenchlingIconName | undefined;
  label: string;
  description: string;
  svgImagePath?: string;
}

export function isEnumOptionsArray(
  choices: ReadonlyArray<string> | ReadonlyArray<number> | ReadonlyArray<EnumOptions>
): choices is ReadonlyArray<EnumOptions> {
  return choices.every((item) => typeof item === 'object');
}

export default function EnumEditor({
  config,
  configShape,
  onChangeConfig,
  uiSchema,
  disabled,
  isExpandable = false,
  isExpanded = false,
  dataTestAttrs = {},
}: EnumEditorProps): JSX.Element {
  // The backend can set disabledMessage on enum shapes to disable the field with a tooltip.
  // This property is not part of the static TS type but is added dynamically by generate_step_shape_and_config.
  const configShapeRecord = configShape as Record<string, unknown>;
  const disabledMessage: string | undefined =
    typeof configShapeRecord.disabledMessage === 'string' ? configShapeRecord.disabledMessage : undefined;
  const isDisabledByMessage = disabledMessage != null && disabledMessage !== '';
  const effectiveDisabled = disabled || isDisabledByMessage;
  function getEnumOptionDisplayName(enumOption: string | number): string {
    return uiSchema?.displayNames?.[enumOption] ?? String(enumOption);
  }

  const placeholder = uiSchema?.placeholder ?? 'Select an option';
  const isCyclingSelector = uiSchema?.renderCyclingSelector;
  let displayName: string = placeholder;
  let selectedIcon: BenchlingIconName | undefined;
  let selectedImage: string | undefined;
  if (isEnumOptionsArray(configShape.choices)) {
    const matchingChoice = configShape.choices.filter((choice) => String(choice.value) === config)[0];
    if (matchingChoice) {
      displayName = matchingChoice.displayName ?? placeholder;
      selectedIcon = matchingChoice.iconName;
      selectedImage = matchingChoice.svgImagePath;
    }
  } else if (config) {
    displayName = getEnumOptionDisplayName(config);
    selectedIcon = uiSchema?.iconNames?.[config];
    selectedImage = uiSchema?.svgImagePaths?.[config];
  }

  assert(
    !(uiSchema?.svgImagePaths != null && uiSchema?.iconNames != null),
    'Cannot have both svgImagePaths and iconNames defined in uiSchema.'
  );

  const data = isEnumOptionsArray(configShape.choices)
    ? configShape.choices.map((choice) => ({
        label: choice.displayName ?? getEnumOptionDisplayName(choice.value),
        value: String(choice.value),
        icon: choice.iconName,
        svgImagePath: choice.svgImagePath,
      }))
    : configShape.choices.map((choice) => ({
        label: getEnumOptionDisplayName(choice),
        value: String(choice),
        icon: uiSchema?.iconNames?.[choice],
        svgImagePath: uiSchema?.svgImagePaths?.[choice],
      }));

  const selector = (
    <Select
      size="md"
      disabled={effectiveDisabled}
      placeholder={isDisabledByMessage ? disabledMessage : displayName}
      value={config == null ? config : String(config)}
      allowDeselect={!configShape.required}
      clearable={!configShape.required}
      data={data}
      itemComponent={SelectItem}
      onChange={(value) => {
        onChangeConfig(value ?? null);
      }}
      className={css(styles.grow)}
      classNames={{
        input: css(
          styles.input,
          isExpandable && styles.expandable,
          isExpandable && isCyclingSelector && styles.expandableCyclingSelectorBorder,
          isExpanded && styles.expanded
        ),
        dropdown: css(isExpandable && isExpanded && styles.expandableDropdown),
      }}
      searchable={true}
      nothingFound="No options"
      iconWidth={selectedImage ? 50 : undefined}
      icon={
        (selectedIcon && <BenchlingIcon icon={selectedIcon} className={css(styles.selectedIcon)} />) ||
        (selectedImage && (
          <div className={css(styles.squareImageContainer)}>
            <img src={selectedImage} alt={displayName} className={css(styles.squareImage)} />
          </div>
        ))
      }
      {...getTestProps(dataTestAttrs)}
    />
  );

  // Wrap in a div so the tooltip can capture hover events on the disabled Select
  const wrappedSelector = isDisabledByMessage ? (
    <Tooltip text={disabledMessage}>
      <div className={css(styles.grow)}>{selector}</div>
    </Tooltip>
  ) : (
    selector
  );

  return isCyclingSelector ? (
    <div className={css(isExpandable && styles.marginRight)}>
      <CyclingSelector
        selector={wrappedSelector}
        disabled={effectiveDisabled}
        options={configShape.choices.map((choice) => choice)}
        selectedOption={config}
        onChange={onChangeConfig}
      />
    </div>
  ) : (
    wrappedSelector
  );
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({icon, svgImagePath, label, ...others}: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap={true}>
        {svgImagePath && <img src={svgImagePath} alt={label} />}
        {icon && <BenchlingIcon icon={icon} />}
        <div>
          <Text size="sm">{label}</Text>
        </div>
      </Group>
    </div>
  )
);

const styles = StyleSheet.create({
  marginRight: {
    marginRight: '5px',
  },
  grow: {
    flexGrow: 1,
  },
  chevron: {
    color: Colors.GRAY_DARKER,
  },
  input: {
    height: '36px',
  },
  selectedIcon: {
    color: Colors.BLUE_DARKER,
  },
  expandableDropdown: {
    marginTop: '-9px',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
  },
  expandable: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '4px',
    borderRight: 'none',
    borderTopRightRadius: '0px',
    borderBottomRightRadius: '0px',
  },
  expanded: {
    borderBottomLeftRadius: '0px',
    borderBottomWidth: '0',
  },
  expandableCyclingSelectorBorder: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '4px',
  },
  squareImageContainer: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
  },
  squareImage: {
    width: '100%',
    height: '100%',
  },
});
